import { makeObservable, observable, computed, action } from 'mobx';
import { ColorGenerator } from '@bringg/react-components';

import RoutesMonitoringStore from './routes-monitoring-store';
import { routesMonitoringRootStore } from './routes-monitoring-root-store';
import { etaCalculatorProvider } from '../../shared/utils/eta-calculator/eta-calculator-provider';

const INIT_ROUTES_COUNT = 3;

class RoutesMonitoringTableViewStore {
	runToColor: { [runId: string]: string } = {};
	_checkedRoutes: Map<number, boolean> = new Map();
	_viewedRoutes: Map<number, boolean> = new Map();
	getRandomColor: () => string;

	scrollTopPosition = 0; // this is used to synchronize scroll top between routes table and timeline component

	monitoringStore: RoutesMonitoringStore;

	constructor() {
		makeObservable(this, {
			runToColor: observable,
			_checkedRoutes: observable,
			_viewedRoutes: observable,
			scrollTopPosition: observable,
			checkedRoutes: computed,
			toggleRouteSelection: action,
			initViewedRoutes: action,
			setScrollTop: action,
			setRouteColor: action
		});

		this.getRandomColor = ColorGenerator.getRandomColor();
		const { routesMonitoringStore } = routesMonitoringRootStore.getStore();
		this.monitoringStore = routesMonitoringStore;
	}

	setRouteColor = (routeId: number) => {
		this.runToColor = { ...this.runToColor, [routeId]: this.getRandomColor() };
	};

	getRouteColor = (routeId: number) => {
		if (!routeId || !this.monitoringStore.tasksByRun.has(routeId)) {
			return;
		}

		if (!this.runToColor[routeId] && routeId) {
			this.setRouteColor(routeId);
		}
		return this.runToColor[routeId];
	};

	toggleRouteSelection = (routeId: number, isSelected: boolean) => {
		this._checkedRoutes.set(routeId, isSelected);
		etaCalculatorProvider.resetRouteCalculation(routeId, !isSelected);
	};

	isRouteSelected = (routeId: number): boolean => {
		return this._viewedRoutes.get(routeId);
	};

	isRouteChecked = (routeId: number): boolean => this._checkedRoutes.get(routeId);

	get checkedRoutes(): number[] {
		const checked = [];
		this._checkedRoutes.forEach((value, key) => {
			if (value && this.monitoringStore.runs.find(r => r.id === key)) {
				checked.push(key);
			}
		});
		return checked;
	}

	get viewedRoutes(): number[] {
		const selected = [];
		this._viewedRoutes.forEach((value, key) => {
			if (value && this.monitoringStore.runs.find(r => r.id === key)) {
				selected.push(key);
			}
		});
		return selected;
	}

	toggleViewRoute = (routeId: number, isViewed: boolean) => {
		this._viewedRoutes.set(routeId, isViewed);
		etaCalculatorProvider.resetRouteCalculation(routeId, !isViewed);
	};

	isRouteViewed = (routeId: number): boolean => {
		return this._viewedRoutes.get(routeId);
	};

	initViewedRoutes = () => {
		if (this.viewedRoutes.length === 0) {
			this.monitoringStore.runs
				.slice(0, Math.min(this.monitoringStore.runs.length, INIT_ROUTES_COUNT))
				.forEach(({ id }) => {
					this._viewedRoutes.set(id, true);
				});
		}
	};

	setScrollTop = top => {
		this.scrollTopPosition = top;
	};
}

export default RoutesMonitoringTableViewStore;
